/**
 * TeleportController — VR teleport: aim, judge, ask the server, blink (XR P2 comfort slice, 2026-09-28).
 *
 * The move-hand stick pushed forward AIMS (an arc from that controller's target ray), letting go HOPS; pulling it back
 * cancels. The landing is judged on the client (lib/xr_locomotion.ts `judgeHop`: ground that faces up, not a roof,
 * dry, no wall on the way, within 5 m) and AGAIN by the server (`xrTeleport`, reducers/xr_teleport.ts — Hard Rule #1:
 * the client never moves itself; the hop is paid from the movement budget). The view fades to black, the request is
 * sent from the dark, the row moves, the client's reconcile follows it, the capsule is re-seated on the ground, the
 * view fades back in. A refusal fades back in where you stood and says why.
 *
 * Everything here is ABSOLUTE world space: the rig (atlas.localX/Y/Z, localYaw) + the controller's pose in the live
 * reference space (the rig's frame). The physics colliders, the water provider and the wall masks are all absolute.
 */
import * as THREE from 'three/webgpu';
import { atlas } from '../../state.js';
import { bus } from '../../bus.js';
import { groundYIn } from '../../lib/grounding.js';
import { waterStillAt } from '../../lib/water_fx.js';
import { wallAt } from '../../lib/structure_masks.js';
import { commonsSeaLevel } from '../../lib/sea_level.js';
import { terrainHeight } from '../../../../shared/terrain.js';
import {
  arcPoints, judgeHop, hopRefusalText, segmentSamples, blinkLevel,
  AIM_START, AIM_RELEASE, XR_HOP_MAX_M, BLINK_OUT_S, BLINK_HOLD_MAX_S, BLINK_IN_S,
  type HopRefusal, type BlinkPhase,
} from '../../lib/xr_locomotion.js';
import { TeleportArc } from './TeleportArc.js';

/** The slice of PhysicsModule the aim needs. */
export interface TeleportPhysics {
  readonly ready: boolean;
  readonly waterMode: string;
  rayHit(from: { x: number; y: number; z: number }, dir: { x: number; y: number; z: number }, maxM: number):
    { x: number; y: number; z: number; nx: number; ny: number; nz: number; dist: number; kind: 'terrain' | 'structure' | 'other' } | null;
  teleportAvatar(x: number, z: number, keepY?: boolean): void;
}

export interface TeleportInput {
  stickY: number;
  frame: XRFrame | null;
  refSpace: XRReferenceSpace | null;
  source: XRInputSource | null;
  physics: TeleportPhysics | null;
  riding: boolean;
  indoors: boolean;
  swimming: boolean;
}

export interface TeleportStats {
  aiming: boolean; phase: BlinkPhase; aims: number; hops: number; accepted: number; refused: number;
  lastVerdict: string | null; lastRefusal: string | null; lastTarget: { x: number; z: number } | null;
  lastFrom: { x: number; z: number } | null; landedAt: { x: number; z: number } | null; arcDots: number; black: number;
}

const HOP_SETTLE_M = 0.35;

export class TeleportController {
  readonly arc = new TeleportArc();
  private aiming = false;
  private verdict: ReturnType<typeof judgeHop> | null = null;
  private landing: { x: number; y: number; z: number } | null = null;
  private phase: BlinkPhase = 'idle';
  private phaseT = 0;
  private target: { x: number; z: number } | null = null;
  private request: 'none' | 'sent' | 'ok' | 'refused' = 'none';
  private sinceHopS = 1e9;
  private readonly stats = { aims: 0, hops: 0, accepted: 0, refused: 0, lastRefusal: null as string | null, lastVerdict: null as string | null, lastTarget: null as { x: number; z: number } | null, lastFrom: null as { x: number; z: number } | null, landedAt: null as { x: number; z: number } | null };
  private readonly q = new THREE.Quaternion();
  private readonly v = new THREE.Vector3();

  /** The blink's blackout level (the vignette mesh draws it). */
  black(): number { return blinkLevel(this.phase, this.phaseT); }

  /** True while the hop owns the moment (the stick must not also walk). */
  get busy(): boolean { return this.aiming || this.phase !== 'idle'; }

  /** One frame. `send` is the reducer call (a promise that rejects with the server's reason). */
  update(dt: number, inp: TeleportInput, send: (x: number, z: number) => Promise<void>): void {
    this.sinceHopS += dt;
    this.stepBlink(dt, inp, send);
    if (this.phase !== 'idle') { this.arc.hide(); return; }

    const push = -inp.stickY; // stick forward is negative y
    if (!this.aiming) {
      if (push > AIM_START) { this.aiming = true; this.stats.aims++; }
      else { this.arc.hide(); return; }
    }
    // Pulling the stick back cancels the aim.
    if (push < -AIM_START) { this.aiming = false; this.arc.hide(); this.verdict = null; return; }
    // Released: go (or say why not).
    if (Math.abs(inp.stickY) < AIM_RELEASE) {
      this.aiming = false;
      this.arc.hide();
      const v = this.verdict;
      if (v && v.ok) this.beginHop(v.x, v.z);
      else if (v && !v.ok && this.landing) bus.emit('atlas:status', { text: `Teleport: ${hopRefusalText(v.reason)}` });
      this.verdict = null;
      return;
    }
    this.aim(inp);
  }

  private aim(inp: TeleportInput): void {
    const rig = { x: atlas.localX, y: atlas.localY, z: atlas.localZ, yaw: atlas.localYaw };
    const cy = Math.cos(rig.yaw), sy = Math.sin(rig.yaw);
    // The controller's pose in the rig frame → absolute (rotation about +Y by the rig yaw, three's convention).
    let ox = rig.x, oy = rig.y + 1.2, oz = rig.z, dx = -sy, dy = 0.5, dz = -cy;
    const pose = inp.source && inp.frame && inp.refSpace ? safePose(inp.frame, inp.source.targetRaySpace, inp.refSpace) : null;
    if (pose) {
      const p = pose.transform.position, o = pose.transform.orientation;
      ox = rig.x + p.x * cy + p.z * sy; oy = rig.y + p.y; oz = rig.z - p.x * sy + p.z * cy;
      this.q.set(o.x, o.y, o.z, o.w);
      this.v.set(0, 0, -1).applyQuaternion(this.q);
      dx = this.v.x * cy + this.v.z * sy; dy = this.v.y; dz = -this.v.x * sy + this.v.z * cy;
    }
    const pts = arcPoints(ox, oy, oz, dx, dy, dz, XR_HOP_MAX_M, rig.x, rig.z);
    const conn = atlas.conn;
    const ground = (x: number, z: number): number => (conn ? groundYIn(conn, atlas.currentWorldId, x, z) : 0);
    const phys = inp.physics && inp.physics.ready ? inp.physics : null;
    let hit: { x: number; y: number; z: number; ny: number } | null = null;
    let cut = pts.length;
    for (let i = 0; i + 5 < pts.length; i += 3) {
      const ax = pts[i], ay = pts[i + 1], az = pts[i + 2], bx = pts[i + 3], by = pts[i + 4], bz = pts[i + 5];
      const len = Math.hypot(bx - ax, by - ay, bz - az);
      if (len < 1e-6) continue;
      const h = phys ? phys.rayHit({ x: ax, y: ay, z: az }, { x: bx - ax, y: by - ay, z: bz - az }, len) : null;
      if (h) { hit = { x: h.x, y: h.y, z: h.z, ny: h.ny }; cut = i + 3; break; }
      const gb = ground(bx, bz);
      if (by <= gb) {
        // The analytic ground (physics not ready, or a chunk not yet collidable): interpolate the crossing.
        const ga = ground(ax, az);
        const f = Math.max(0, Math.min(1, (ay - ga) / Math.max(1e-6, (ay - ga) - (by - gb))));
        const x = ax + (bx - ax) * f, z = az + (bz - az) * f;
        hit = { x, y: ground(x, z), z, ny: groundNy(ground, x, z) };
        cut = i + 3;
        break;
      }
    }
    if (!hit && pts.length >= 3) {
      // The arc ended in the air at the range circle: drop straight down from its last point.
      const lx = pts[pts.length - 3], ly = pts[pts.length - 2], lz = pts[pts.length - 1];
      const h = phys ? phys.rayHit({ x: lx, y: ly, z: lz }, { x: 0, y: -1, z: 0 }, 120) : null;
      if (h) hit = { x: h.x, y: h.y, z: h.z, ny: h.ny };
      else { const g = ground(lx, lz); if (g <= ly) hit = { x: lx, y: g, z: lz, ny: groundNy(ground, lx, lz) }; }
      if (hit) pts.push(hit.x, hit.y, hit.z);
    }
    const drawn = hit ? pts.slice(0, cut).concat([hit.x, hit.y, hit.z]) : pts;
    // A wall on the way: the walking-height masks along the segment (the server's walls) and the physics colliders
    // at chest height between the two feet.
    let wallOnPath = false;
    if (hit) {
      for (const [x, z] of segmentSamples(rig.x, rig.z, hit.x, hit.z, 0.35)) { if (wallAt(x, z)) { wallOnPath = true; break; } }
      if (!wallOnPath && phys) {
        const ay = rig.y + 1.1, by = hit.y + 1.1;
        const dx2 = hit.x - rig.x, dy2 = by - ay, dz2 = hit.z - rig.z;
        const len = Math.hypot(dx2, dy2, dz2);
        if (len > 0.8) {
          const h = phys.rayHit({ x: rig.x, y: ay, z: rig.z }, { x: dx2, y: dy2, z: dz2 }, len - 0.4);
          if (h && h.kind === 'structure') wallOnPath = true;
        }
      }
    }
    const aboveGround = hit ? hit.y - ground(hit.x, hit.z) : 0;
    const water = hit ? waterStillAt(hit.x, hit.z) : null;
    // The server's own sea rule (reducers/xr_teleport.ts): in the Commons the analytic ground at the landing must stand
    // above the derived sea level + 0.5. The rendered water provider can be mid-bake after a long move; this cannot.
    let seaRule = false;
    if (hit && conn && atlas.commonsWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId) {
      const seed = conn.db.world.worldId.find(atlas.commonsWorldId)?.terrainSeed;
      if (seed) seaRule = terrainHeight(seed, hit.x, hit.z) <= commonsSeaLevel(seed) + 0.5;
    }
    const v = judgeHop({
      fromX: rig.x, fromZ: rig.z,
      hit: hit ? { x: hit.x, y: hit.y, z: hit.z, ny: hit.ny, aboveGround } : null,
      waterLevel: water, seaRule, wallOnPath,
      riding: inp.riding, indoors: inp.indoors, swimming: inp.swimming,
      sinceLastHopS: this.sinceHopS,
    });
    this.verdict = v;
    // Over water the landing is on the bed, under the surface: draw the marker ON the water so the refusal is seen.
    this.landing = hit ? { x: hit.x, y: water !== null && water > hit.y ? water : hit.y, z: hit.z } : null;
    this.stats.lastVerdict = v.ok ? 'ok' : v.reason;
    this.arc.show(drawn, this.landing, v.ok);
  }

  private beginHop(x: number, z: number): void {
    this.target = { x, z };
    this.stats.lastTarget = { x, z };
    this.stats.lastFrom = { x: atlas.localX, z: atlas.localZ };
    this.stats.hops++;
    this.sinceHopS = 0;
    this.request = 'none';
    this.phase = 'out';
    this.phaseT = 0;
  }

  private stepBlink(dt: number, inp: TeleportInput, send: (x: number, z: number) => Promise<void>): void {
    if (this.phase === 'idle') return;
    this.phaseT += dt;
    if (this.phase === 'out' && this.phaseT >= BLINK_OUT_S) {
      // Black: ask the server from the dark.
      this.phase = 'hold'; this.phaseT = 0;
      const t = this.target;
      if (!t) { this.phase = 'in'; return; }
      this.request = 'sent';
      send(t.x, t.z).then(() => { this.request = 'ok'; this.stats.accepted++; }, (err: unknown) => {
        this.request = 'refused';
        this.stats.refused++;
        const msg = String((err as Error)?.message ?? err).replace(/^.*SenderError:\s*/, '').slice(0, 100);
        this.stats.lastRefusal = msg;
        bus.emit('atlas:status', { text: `Teleport refused: ${msg}` });
      });
      return;
    }
    if (this.phase === 'hold') {
      const t = this.target;
      const arrived = t ? Math.hypot(atlas.localX - t.x, atlas.localZ - t.z) <= HOP_SETTLE_M : true;
      if (this.request === 'refused' || (this.request === 'ok' && arrived) || this.phaseT >= BLINK_HOLD_MAX_S) {
        if (this.request === 'ok' && t) {
          // Re-seat on the ground at the landing (the reconcile's smooth path keeps the old height).
          if (inp.physics?.ready) { try { inp.physics.teleportAvatar(atlas.localX, atlas.localZ); } catch { /* physics poisoned — the watchdog recovers */ } }
          this.stats.landedAt = { x: atlas.localX, z: atlas.localZ };
        }
        this.phase = 'in'; this.phaseT = 0;
      }
      return;
    }
    if (this.phase === 'in' && this.phaseT >= BLINK_IN_S) { this.phase = 'idle'; this.phaseT = 0; this.target = null; }
  }

  /** Session end / mode change: drop everything. */
  reset(): void {
    this.aiming = false;
    this.verdict = null;
    this.landing = null;
    this.phase = 'idle';
    this.phaseT = 0;
    this.target = null;
    this.arc.hide();
  }

  state(): TeleportStats {
    return {
      aiming: this.aiming, phase: this.phase, aims: this.stats.aims, hops: this.stats.hops, accepted: this.stats.accepted,
      refused: this.stats.refused, lastVerdict: this.stats.lastVerdict, lastRefusal: this.stats.lastRefusal,
      lastTarget: this.stats.lastTarget, lastFrom: this.stats.lastFrom, landedAt: this.stats.landedAt,
      arcDots: this.arc.dotCount, black: +this.black().toFixed(3),
    };
  }

  dispose(): void { this.arc.dispose(); }
}

function safePose(frame: XRFrame, space: XRSpace, ref: XRReferenceSpace): XRPose | null {
  try { return frame.getPose(space, ref) ?? null; } catch { return null; }
}

/** The analytic ground's up-normal component at (x, z) by central differences. */
function groundNy(ground: (x: number, z: number) => number, x: number, z: number): number {
  const e = 0.6;
  const gx = (ground(x + e, z) - ground(x - e, z)) / (2 * e);
  const gz = (ground(x, z + e) - ground(x, z - e)) / (2 * e);
  return 1 / Math.hypot(gx, 1, gz);
}

export type { HopRefusal };
